import {
  getTemplateBySlug,
  isCoachingAdvisorySlug,
  type ProxyRole,
} from "@/lib/proxy-templates";
import type { ProxyTemplateSnapshot } from "@/lib/template-snapshot";

export const COACHING_LANDING_CARD = {
  title: "Make Every Coaching Session Count",
  description:
    "Collect progress, problems, decisions, and priorities before the call so you can spend live time on the work that actually needs you.",
  cta: "Create Pre-Session Proxy",
  templateSlug: "pre-session-brief",
} as const;

export const COACHING_TEMPLATE_SLUGS = [
  "pre-session-brief",
  "weekly-progress-check-in",
  "commitment-check-in",
  "post-session-reflection",
  "executive-debrief",
  "decision-preparation",
  "founder-check-in",
  "leadership-situation-brief",
  "custom-coach-intake",
] as const;

export function resolveSnapshotProxyRole(
  snapshot: Pick<ProxyTemplateSnapshot, "slug" | "proxyRole"> | null | undefined,
): ProxyRole | null {
  if (!snapshot) return null;
  if (snapshot.proxyRole) return snapshot.proxyRole;
  if (isCoachingAdvisorySlug(snapshot.slug)) return "session_assistant";
  return null;
}

export function getCoachingEntryTitle(input: {
  templateSlug: string | null | undefined;
  creatorName: string;
}): string {
  const creator = input.creatorName || "your coach";
  const slug = input.templateSlug ?? "";
  switch (slug) {
    case "pre-session-brief":
      return `Pre-session check-in for ${creator}`;
    case "weekly-progress-check-in":
      return `Progress check-in for ${creator}`;
    case "commitment-check-in":
      return `Commitment check-in for ${creator}`;
    case "post-session-reflection":
      return `Post-session reflection for ${creator}`;
    case "executive-debrief":
      return `Executive debrief for ${creator}`;
    case "decision-preparation":
      return `Decision preparation for ${creator}`;
    case "founder-check-in":
      return `Founder check-in for ${creator}`;
    case "leadership-situation-brief":
      return `Leadership brief for ${creator}`;
    default:
      return `Check-in for ${creator}`;
  }
}

export function getCoachingEntryBody(input: {
  proxyRole: ProxyRole;
  creatorName: string;
  objective?: string | null;
}): string {
  const creator = input.creatorName || "your coach";
  switch (input.proxyRole) {
    case "guided_coach":
      return `This is an AI coaching proxy created by ${creator}. You can use it to reflect, think through issues, and prepare between your live sessions. A summary of your conversation will be shared with ${creator}.`;
    case "custom": {
      const purpose = input.objective?.trim();
      if (purpose) {
        return `This AI Proxy was created by ${creator} to help with: ${purpose}. A summary of your conversation will be shared with ${creator}.`;
      }
      return `This AI Proxy was created by ${creator} to collect information before or between your sessions. A summary of your conversation will be shared with ${creator}.`;
    }
    case "session_assistant":
    default:
      return `This AI Proxy will collect a short update before your coaching session. A summary of your responses will be shared with ${creator}.`;
  }
}

export function getCoachingConsentDisclosure(creatorName: string): string {
  const creator = creatorName || "the creator";
  return `This AI Proxy was created by ${creator}. A summary and transcript of this conversation may be available to them.`;
}

export function getCoachingCompletionMessage(input: {
  templateSlug: string | null | undefined;
  creatorName: string;
}): string {
  const creator = input.creatorName || "the creator";
  const slug = input.templateSlug ?? "";
  switch (slug) {
    case "pre-session-brief":
      return `Your session brief has been sent to ${creator}.`;
    case "weekly-progress-check-in":
      return `Your progress update has been sent to ${creator}.`;
    case "commitment-check-in":
      return `Your commitment check-in has been sent to ${creator}.`;
    case "post-session-reflection":
      return `Your reflection has been sent to ${creator}.`;
    case "executive-debrief":
      return `Your debrief has been sent to ${creator}.`;
    case "decision-preparation":
      return `Your decision preparation summary has been sent to ${creator}.`;
    case "founder-check-in":
      return `Your founder check-in has been sent to ${creator}.`;
    case "leadership-situation-brief":
      return `Your leadership brief has been sent to ${creator}.`;
    default:
      return `Your update has been sent to ${creator}.`;
  }
}

export function getCoachingTemplateDefaultRole(slug: string): ProxyRole | null {
  const template = getTemplateBySlug(slug);
  if (!template || template.category !== "coaching_advisory") return null;
  return template.proxyRole ?? "session_assistant";
}
